import { useCallback, useEffect, useRef, useState } from 'react';
import { SignupError, getSignupStatus } from '../../services/signup';

/** How often, and for how long, to ask whether Stripe's webhook has unlocked the organization. */
export const ACTIVATION_POLL_INTERVAL_MS = 2000;
export const ACTIVATION_POLL_TIMEOUT_MS = 60000;

/**
 * `signInRequired` and `refused` are permanent answers (401 and 403): waiting longer cannot change
 * them, so polling stops and the page says what to do instead.
 */
export type ActivationState = 'polling' | 'active' | 'timedOut' | 'signInRequired' | 'refused';

/**
 * Polls GET /api/signup/status until the organization stops awaiting payment, or the timeout.
 *
 * Stripe redirects to the success URL as soon as the card clears, which can be before the webhook
 * that unlocks the organization has landed, so "not yet" is the expected first answer. A failed
 * request is treated as "not yet" too: the timeout state already offers a retry, and one network
 * blip must not end the wait early. The exceptions are a 401 or 403, which no amount of waiting
 * fixes, and which end the window at once.
 *
 * `retry` starts a fresh window. Each window gets an id, so a request still in flight from the one
 * before it cannot report into the new one, and unmounting stops everything.
 */
export const usePaymentActivation = () => {
  const [state, setState] = useState<ActivationState>('polling');
  const windowIdRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  const stop = useCallback(() => {
    windowIdRef.current += 1;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const begin = useCallback(() => {
    stop();
    const windowId = windowIdRef.current;
    const deadline = Date.now() + ACTIVATION_POLL_TIMEOUT_MS;
    setState('polling');

    const poll = async () => {
      let active = false;
      let permanent: ActivationState | null = null;
      try {
        const status = await getSignupStatus();
        active = !status.awaitingPayment;
      } catch (error: unknown) {
        if (error instanceof SignupError && error.status === 401) {
          permanent = 'signInRequired';
        } else if (error instanceof SignupError && error.status === 403) {
          permanent = 'refused';
        }
        // Anything else: not yet; see above.
      }

      if (windowIdRef.current !== windowId) return;

      if (permanent) {
        setState(permanent);
        return;
      }

      if (active) {
        setState('active');
        return;
      }

      if (Date.now() + ACTIVATION_POLL_INTERVAL_MS > deadline) {
        setState('timedOut');
        return;
      }

      timerRef.current = window.setTimeout(() => void poll(), ACTIVATION_POLL_INTERVAL_MS);
    };

    void poll();
  }, [stop]);

  useEffect(() => {
    begin();
    return stop;
  }, [begin, stop]);

  return { state, retry: begin };
};
